import React, { useState } from "react";
import { MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { SERVICES } from "../data/services";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full border border-[#00205B]/30 bg-white px-4 py-3 text-sm nnc-text placeholder:text-[#00205B]/40 focus:outline-none focus:border-[#FCB814] focus:ring-1 focus:ring-[#FCB814] transition-colors";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", service_interest: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "Please check your entries and try again."
        );
      }
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-white" data-testid="contact-hero">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-5 gap-14">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-3 mb-6">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">Contact</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight nnc-text">
                Start the conversation.
              </h1>
              <p className="mt-8 text-base leading-relaxed nnc-text">
                Whether you are forming a new nonprofit, strengthening your
                board, pursuing government contracts, or planning your next
                funding strategy — tell us where you are and where you want
                to go. We respond to every inquiry.
              </p>
              <div className="mt-10 flex items-center gap-3 nnc-text">
                <MapPin className="h-5 w-5 nnc-gold-text" strokeWidth={2.5} />
                <span className="font-bold tracking-wide">
                  Morgantown, West Virginia · United States
                </span>
              </div>
            </div>

            <div className="lg:col-span-3">
              {status === "sent" ? (
                <div
                  className="border border-[#00205B] px-10 py-16 text-center"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="h-10 w-10 nnc-gold-text mx-auto" />
                  <h2 className="mt-6 text-lg font-bold nnc-text">
                    Your inquiry has been sent.
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed nnc-text max-w-md mx-auto">
                    Thank you for reaching out to Nonprofit Navigation
                    Consulting. We have received your message and will be in
                    touch shortly.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="border border-[#00205B]/15 bg-[#FBF2DE]/40 p-8 lg:p-10"
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2">
                        Full Name *
                      </label>
                      <input
                        required
                        minLength={2}
                        maxLength={120}
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your name"
                        className={inputCls}
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2">
                        Email Address *
                      </label>
                      <input
                        required
                        type="email"
                        maxLength={200}
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@organization.org"
                        className={inputCls}
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2">
                      Organization
                    </label>
                    <input
                      maxLength={200}
                      value={form.organization}
                      onChange={set("organization")}
                      placeholder="Organization or agency (optional)"
                      className={inputCls}
                      data-testid="contact-organization-input"
                    />
                  </div>
                  <div className="mt-5">
                    <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2">
                      Area of Interest
                    </label>
                    <select
                      value={form.service_interest}
                      onChange={set("service_interest")}
                      className={inputCls}
                      data-testid="contact-service-select"
                    >
                      <option value="">General inquiry / not sure yet</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-5">
                    <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2">
                      How can we help? *
                    </label>
                    <textarea
                      required
                      minLength={10}
                      maxLength={5000}
                      rows={6}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your organization, your goals, and the support you're looking for…"
                      className={inputCls}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <input
                    type="text"
                    value={form.website}
                    onChange={set("website")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                  />
                  {error && (
                    <p
                      className="mt-4 text-sm font-bold text-red-700"
                      data-testid="contact-error"
                    >
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="nnc-btn mt-8 disabled:opacity-60"
                    data-testid="contact-submit-button"
                  >
                    {status === "sending" ? (
                      <>
                        Sending… <Loader2 className="h-4 w-4 animate-spin" />
                      </>
                    ) : (
                      <>
                        Send Inquiry <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
